// useOptions.ts
import type { AddressState } from '@hooks/useAutocompleteAddress'
import { useDebounce } from '@hooks/useDebounce'
import { useLocale } from '@hooks/useLocale'
import { getPublicConfigVariable } from '@lib/utils/getConfigVariable'
import getRegionFromLocale from '@lib/utils/getRegionFromLocale'
import isEqual from 'lodash.isequal'
import { useState } from 'react'
import useSWRImmutable from 'swr/immutable'
import useTranslateMessage from './useTranslateMessage'

export type FieldType = 'street' | 'streetNumber' | 'floor' | 'household' | 'zip' | 'city'

export interface Street {
  countryCode: string
  city: string
  streetName: string
  streetIds: number[]
  isAliasMatch: string
  streetNo: number
}

export interface StreetNumber {
  streetNo: number
  addressId: number
  entrance: string | null
  houseType: string
  postalCode: string
  duplicateNumberAndEntrance: boolean
  latitude: number
  longitude: number
  showHouseholds: boolean
  streetId: string
  deliveryPointId: number
}

export interface Floor {
  floorNo: number
  floorType: string
  floorTypeName: string | null
  parentDeliveryPointId: number
}

export interface Household {
  flatNo: number
  flatNoAlias: string
  deliveryPointId: string
  parentDeliveryPointId: number
}

export interface OptionsResponse<T> {
  id: number
  value: T
  label: string
}

export interface Options {
  streets: OptionsResponse<Street>[]
  streetNumbers: OptionsResponse<StreetNumber>[]
  floors: OptionsResponse<Floor>[]
  households: OptionsResponse<Household>[]
}

export const useAutocompleteOptions = () => {
  const locale = useLocale()
  const tenant = getRegionFromLocale(locale)
  const { t } = useTranslateMessage()

  const baseUrl = getPublicConfigVariable('ADDRESS_AUTOCOMPLETE_API_URL')
  const apiKey = getPublicConfigVariable('ADDRESS_AUTOCOMPLETE_API_KEY')

  const searchInitialState: { term: string; field: keyof Options; address: AddressState | null } = {
    term: '',
    field: 'streets',
    address: null
  }
  const [search, setSearch] = useState<typeof searchInitialState>(searchInitialState)

  const optionsInitialState: Options = {
    streets: [],
    streetNumbers: [],
    floors: [],
    households: []
  }
  const [options, setOptions] = useState<Options>(optionsInitialState)

  const fetcher = async (url: string, headers: any) => {
    const response = await fetch(url, { headers })
    return response.json()
  }

  const buildRoute = (type: keyof Options, searchTerm: string, address: AddressState | null) => {
    try {
      const encodedSearchTerm = encodeURIComponent(searchTerm)
      const encodeAddressState = (addressState: any) => {
        const encodeValue = (value: any): any => {
          if (Array.isArray(value)) {
            return value.map(encodeValue)
          }
          if (value !== null && typeof value === 'object') {
            return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, encodeValue(v)]))
          }
          if (typeof value === 'string' || typeof value === 'number') {
            return encodeURIComponent(value.toString())
          }
          return value
        }

        return Object.fromEntries(Object.entries(addressState).map(([key, value]) => [key, encodeValue(value)]))
      }

      const adr = address && encodeAddressState(address)

      switch (type) {
        case 'streets':
          return `${baseUrl}/getStreetNames?q=${encodedSearchTerm}`
        case 'streetNumbers':
          if (!adr?.street?.streetIds) throw new Error('Street IDs are undefined')
          return `${baseUrl}/getStreetNumbers?q=${encodedSearchTerm}&streetIds=${adr?.street.streetIds.join(',')}`
        case 'floors':
          if (!adr?.streetNumber?.deliveryPointId) throw new Error('Delivery Point ID is undefined')
          return `${baseUrl}/getFloors?q=${encodedSearchTerm}&houseId=${adr?.streetNumber.deliveryPointId}`
        case 'households':
          if (!adr?.floor?.floorNo || !adr?.floor?.floorType || !adr?.streetNumber?.deliveryPointId)
            throw new Error('Floor details or Delivery Point ID are undefined')
          return `${baseUrl}/getApartments?q=${encodedSearchTerm}&floor=${adr.floor.floorNo}&floorType=${adr.floor.floorType}&houseId=${adr?.streetNumber.deliveryPointId}`
        default:
          return ''
      }
    } catch (error) {
      return null
    }
  }

  const transformOptions = (type: any, data: any, address: AddressState | null): OptionsResponse<any>[] => {
    switch (type) {
      case 'streets':
        return (data as Street[]).map(
          (i, id): OptionsResponse<Street> => ({
            id,
            value: i,
            label: `${i.streetName}, ${i.city}`
          })
        )
      case 'streetNumbers':
        return (data as StreetNumber[]).map(
          (i, id): OptionsResponse<StreetNumber> => ({
            id,
            value: i,
            label: `${i.streetNo}${i.entrance ?? ''}`
          })
        )
      case 'floors':
        return (data as Floor[]).map(
          (i, id): OptionsResponse<Floor> => ({
            id,
            value: i,
            label: t(
              {
                id: 'address.floor.label',
                defaultMessage: '{floorNo}. floor {floorType}'
              },
              {
                floorNo: i.floorNo,
                floorType: i.floorType === 'U' ? t({ id: 'floor.type.basement', defaultMessage: '(basement)' }) : ''
              }
            )
          })
        )
      case 'households':
        return (data as Household[]).map((i, id): OptionsResponse<Household> => {
          const generateIdentifier = (floorNo: number, floorType: string, flatNo: number) => {
            const floorPart = floorNo > 10 ? floorNo : `0${floorNo}`
            const flatPart = flatNo > 10 ? flatNo : `0${flatNo}`
            return t(
              {
                id: 'household.identifier',
                defaultMessage: '{flatNo}. counting from left ({floorType}{floorPart}{flatPart})'
              },
              { flatNo, floorType, floorPart, flatPart }
            )
          }
          return {
            id,
            value: i,
            label: generateIdentifier(
              Number(address?.floor?.floorNo) ?? 1,
              address?.floor?.floorType ?? '',
              Number(i.flatNo)
            )
          }
        })
      default:
        return data
    }
  }

  const debouncedSearchTerm = useDebounce(search.term, 500)
  const route = buildRoute(search.field, debouncedSearchTerm, search.address)

  const { isLoading, data, error } = useSWRImmutable(
    debouncedSearchTerm && debouncedSearchTerm === search.term ? route : null,
    (url) =>
      fetcher(url, {
        'x-api-key': apiKey,
        country: tenant,
        'Content-Type': 'application/json',
        Accept: 'application/json'
      })
  )

  let newOptionsList = options
  let hasNewOptions = false
  if (!isLoading && !error && data && data[search.field].length > 0) {
    const newOptions = transformOptions(search.field, data[search.field], search.address)
    const currentOptions = options[search.field]
    hasNewOptions = !isEqual(currentOptions, newOptions)

    newOptionsList = {
      ...options,
      [search.field]: newOptions
    }

    if (hasNewOptions) {
      setOptions((prevOptions) => ({ ...prevOptions, [search.field]: newOptions }))
    }
  }

  const nextFields: any = {
    streets: ['streetNumbers', 'floors', 'households'],
    streetNumbers: ['floors', 'households'],
    floors: ['households'],
    households: []
  }

  const clearOptions = (type: keyof Options) => {
    setOptions((prevState) => {
      const newState = { ...prevState }
      const fieldsToClear = nextFields[type] || []
      fieldsToClear.forEach((field: keyof Options) => {
        newState[field] = []
      })
      return newState
    })
  }

  return {
    options: newOptionsList,
    getOptions: (type: keyof Options, searchTerm: string, context: any) => {
      setSearch({ term: searchTerm, field: type, address: context?.address })
    },
    optionsLoading: isLoading,
    optionsError: error,
    invalidateOptions: clearOptions
  }
}
